define((require) => {
    "use strict";

    const {clone}       = require("underscore");
    const ImprovedModel = require("shared/zbone/ImprovedModel");
    const SyncManager   = require("shared/services/sync/SyncManager");
    const Permissions   = require("shared/security/Permissions");
    const Publishables  = require("ui/publish/Publishables");
    const RestApi       = require("shared/services/RestApi");

    // TODO Rename to ChannelModel
    /**
     * @class {Channel}
     * Immutable editor type only, viewer used representation from S3.
     */
    const Channel = ImprovedModel.extend("Slot", { // a channel is a slot in user terminology

        url() {
            if (this.isNew()) {
                return RestApi.pathToCollection(RestApi.Collections.CHANNELS);
            }
            return RestApi.pathToSpecificResource(RestApi.Collections.CHANNELS, this.get("id"));
        },

        defaults: {
            name:             "(Untitled Slot)",
            selector:         "",
            url:              null,

            experience:       null,  // these two are mutually exclusive
            group:            null,  //
            publishable:      null,
            restoreType:      null,  // supported by the backend and channels.js, but currently
            loadingIndicator: null,  // not editable, but defined for clarity and consistency!
            preload:          null,  //
            timeout:          null,  //
            cacheable:        null,  //
            updated:          false, // if channel/slot was updated/modified by user in dnd or manual publishing
            dnd:              null,  // if created in DnD
            manual:           null,  // if created/modified via manual publishing
            schedules:        []
        },

        transient: ["resolvedReferences"],

        // Collapse resolved Experiences into references again (in json) to avoid to much data being submitted. We
        // have to submit entire groups as the "type" property is needed to marshal it successfully...
        transformBeforeSync(options) {
            if (options.sync !== "fetch" && options.sync !== "marshal") { // marshal handled by DnD!
                return [{
                    name: "publishable",
                    transform: function(publishable) {
                        return Publishables.toReference(publishable);
                    }
                }, {
                    name: "experience",
                    transform: function(experience) {
                        return Publishables.toReference(experience);
                    }
                }, {
                    name: "group",
                    transform: function(group) {
                        return Publishables.toReference(group);
                    }
                }, {
                    name: "schedules",
                    transform: function(schedules) {
                        let i = 0;
                        const clonedScheduled = [];

                        if (schedules) {
                            for (i = 0; i < schedules.length; i++) {
                                const schedule = clone(schedules[i]);
                                // At least one will be null!
                                schedule.experience = Publishables.toReference(schedule.experience);
                                schedule.group = Publishables.toReference(schedule.group);
                                schedule.publishable = schedule.experience ||  schedule.group;

                                delete schedule.resolvedReferences;
                                clonedScheduled.push(schedule);
                            }
                        }
                        return clonedScheduled;
                    }
                }];
            }
        },

        computed: {
            publishable: {
                deps: ["experience", "group", "resolvedReferences"],
                value: function(experience, group, resolvedReferences) {
                    const publishable = experience || group;
                    return Publishables.resolveReference(publishable, resolvedReferences);
                },
                transient: false
            },
            experience: {
                deps: ["experience", "resolvedReferences"],
                value: function(experience, resolvedReferences) {
                    return Publishables.resolveReference(experience, resolvedReferences);
                },
                transient: false
            },
            group: {
                deps: ["group", "resolvedReferences"],
                value: function(group, resolvedReferences) {
                    return  Publishables.resolveReference(group, resolvedReferences);
                },
                transient: false
            },
            schedules: {
                deps: ["schedules"],
                value: function(schedules) {
                    var schedule,
                        i = 0;

                    if (schedules) {
                        for (i = 0; i < schedules.length; i++) {
                            schedule = schedules[i];
                            // At least one will be null!
                            schedule.experience =  Publishables.resolveReference(schedule.experience, schedule.resolvedReferences);
                            schedule.group =  Publishables.resolveReference(schedule.group, schedule.resolvedReferences);
                            schedule.publishable =  schedule.experience ? schedule.experience : schedule.group;
                        }
                    }
                    return schedules;
                },
                transient: false
            }
        },

        _distinguishedName() {
            return this.get("name");
        },

        getFirstSchedule() {
            return this.get("schedules")[0];
        },

        hasLive() {
            return Boolean(this.get("publishable"));
        },

        hasSchedule() {
            const schedules = this.get("schedules");
            return schedules && schedules.length === 1;
        }

    }, {
        push() {
            return SyncManager.sync({
                method: "push",
                role:   Permissions.Roles.PUBLISH, // must be at least a publisher to update other users
                fn: {
                    url:       RestApi.pathToCollection(RestApi.Collections.CHANNELS_PUSH),
                    type:     "PUT",
                    dataType: "json"
                },
                resultHandler(response) {
                    return response;
                }
            }).promise();
        },

        hasUnPushedChannels() {
            return SyncManager.sync({
                method: "request for un-pushed slots",
                role:   Permissions.Roles.PUBLISH, // must be at least a publisher to update other users
                fn: {
                    url:       RestApi.pathToCollection(RestApi.Collections.CHANNELS_UNPUSHED),
                    type:     "GET"
                },
                resultHandler(response) {
                    return response; // for this example, we want the server response, not "experience"
                }
            }).promise();
        },

        publishCurrentExperience(channelJson, experience = null) {
            const reference = Publishables.toReference(experience);

            channelJson.experience = reference;
            channelJson.group = null;
            channelJson.publishable = reference;
        },

        publishCurrentGroup(channelJson, group = null) {
            const reference = Publishables.toReference(group);

            channelJson.group = reference;
            channelJson.experience = null;
            channelJson.publishable = reference;
        },

        publishScheduledExperience(channelJson, experience, startTime) {
            Channel.publishScheduled(channelJson, experience, "experience", startTime); // all or nothing, group will be cleared by this
        },

        publishScheduledGroup(channelJson, group, startTime) {
            Channel.publishScheduled(channelJson, group, "group", startTime); // all or nothing, experience will be cleared by this
        },

        publishScheduled(channelJson, model, type, startTime) {
            const schedules = [];

            // Schedule content...
            if (model) {
                const schedule = {
                    startTime: parseInt(startTime, 10),
                    publishable: Publishables.toReference(model)
                };

                schedule[type] = model;

                schedules.push(schedule);

            // Schedule "No Experience" from "startTime"...
            } else if (startTime) {
                schedules.push({
                    startTime: parseInt(startTime, 10),
                    takeDownCurrent: true
                });
            }
            // Or remove scheduled model altogether (empty)...
            channelJson.schedules = schedules;
        },

        hasContent(channelJson) {
            return channelJson && (channelJson.experience || channelJson.group);
        },

        destroy(channelJson) {
            return new Channel(channelJson).destroy();
        }

    });

    return Channel;
});



// WEBPACK FOOTER //
// ./src/editor/ui/publish/channelSetup/Channel.js